import { showNotification } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { QUERY_KEY } from "../components/grupos-diagnostico-table/GruposDiagnosticoTable";
import { reorderGrupos } from "../firestore";

type ReorderParameters = {
  idQuestionario: string;
  grupoId: string;
  newPosition: number;
};

interface UseGruposReorderOptions {
  /** Called when reorder fails - allows component to reset optimistic state */
  onError?: () => void;
  /** Called when reorder succeeds */
  onSuccess?: () => void;
}

/**
 * Hook to handle grupo reordering via drag and drop.
 * Optimistic UI is handled by the table component using local state.
 * @param options
 */
function useGruposReorder(options: UseGruposReorderOptions = {}) {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<unknown, FirestoreError, ReorderParameters>({
    mutationFn: async ({ idQuestionario, grupoId, newPosition }) => {
      if (!activeEmpresaId) {
        throw new Error("Nenhuma empresa selecionada");
      }
      return reorderGrupos(activeEmpresaId, idQuestionario, grupoId, newPosition);
    },
    onSuccess: async () => {
      // Refetch to sync with server after successful mutation
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      options.onSuccess?.();
    },
    onError: (error) => {
      // Refetch to restore correct state on error
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      // Notify component to clear optimistic state
      options.onError?.();

      captureException(error, true);
      showNotification({
        message: "Erro ao reordenar grupos. Tente novamente.",
        color: "red",
        autoClose: 3000
      });
    }
  });

  const handleReorder = (idQuestionario: string, grupoId: string, newPosition: number) => {
    mutate({ idQuestionario, grupoId, newPosition });
  };

  return { handleReorder, isReordering: isPending };
}

export default useGruposReorder;
